import React, { FC, useCallback, useMemo } from "react";
import {
  Alert,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Box from "../Box";
import { useAppContext } from "../AppContext";
import useWeek from "./useWeek";
import useCenterRadiologists from "./useCenterRadiologists";
import useRadiologistCutoffs from "./useRadiologistCutoffs";
import useEditMode from "./useEditMode";
import useCalendarEditState from "./useCalendarEditState";
import useStatConstraints from "./useStatConstraints";
import useSaveCalendar from "./useSaveCalendar";
import ConflictAlert from "./ConflictAlert";
import AvailabilityToolbar from "./AvailabilityToolbar";
import AvailabilityTableRow from "./AvailabilityTableRow";
import useStyles from "./useStyles";
import { buildDayColumns } from "./calendarUtils";

const SKELETON_ROWS = 3;

const RadiologistAvailabilityScreen: FC = () => {
  const { nameCell, dayHeaderCell, emptyHeaderCell, bodyCell } = useStyles();
  const { databaseUserData, authUserData } = useAppContext();
  const centerId = databaseUserData.primaryRole.center_id;
  const currentUserId = authUserData.sub;

  // --- data ---
  const { weekStart, weekEnd, onPrevClick, onNextClick } = useWeek();
  const { data: radiologists = [], isPending: isRadiologistsPending } =
    useCenterRadiologists(centerId);
  const { data: cutoffs = [], isPending: isCutoffsPending } =
    useRadiologistCutoffs(centerId, weekStart, weekEnd);

  const isTablePending = isRadiologistsPending || isCutoffsPending;

  // Always show Mon–Sat (ISO days 1–6); Sunday is always off
  const MON_TO_SAT = ["1", "2", "3", "4", "5", "6"];
  const dayColumns = useMemo(
    () => buildDayColumns(MON_TO_SAT, weekStart),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekStart],
  );
  const dates = useMemo(
    () => dayColumns.map((col) => col.dateISO),
    [dayColumns],
  );
  const radiologistIds = useMemo(
    () => radiologists.map((r) => r.id),
    [radiologists],
  );

  // --- edit mode ---
  const {
    isEditMode,
    canEdit,
    isRadiologistOnly,
    editableRadiologistIds,
    onEditClick,
    onCancelEdit,
  } = useEditMode({
    permissions: databaseUserData.permissions,
    currentUserId,
    radiologists,
  });

  const { getValue, setValue, resetToInitial } = useCalendarEditState({
    cutoffs,
    radiologistIds,
    dates,
  });

  const { getDisabledValues, getSlotBounds } = useStatConstraints({
    centerIds: databaseUserData.centerIds,
    currentUserId,
    weekStart,
    weekEnd,
    enabled: isEditMode,
  });

  const handleEditClick = useCallback(() => {
    resetToInitial(cutoffs);
    onEditClick();
  }, [cutoffs, onEditClick, resetToInitial]);

  // --- save ---
  const {
    isSaving,
    saveError,
    clearSaveError,
    pendingConflicts,
    conflictAlertOpen,
    handleSave,
    handleConflictConfirm,
    handleConflictEdit,
  } = useSaveCalendar({
    centerId,
    weekStart,
    weekEnd,
    editableRadiologistIds,
    dates,
    getValue,
    isRadiologistOnly,
    onSaveSuccess: onCancelEdit,
  });

  return (
    <Box>
      <AvailabilityToolbar
        weekStart={weekStart}
        weekEnd={weekEnd}
        onPrevClick={onPrevClick}
        onNextClick={onNextClick}
        isEditMode={isEditMode}
        canEdit={canEdit}
        isTablePending={isTablePending}
        isSaving={isSaving}
        onEditClick={handleEditClick}
        onCancelEdit={onCancelEdit}
        onSave={handleSave}
      />

      {saveError && (
        <Box pb={2}>
          <Alert severity="error" onClose={clearSaveError}>
            {saveError}
          </Alert>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell className={emptyHeaderCell} />
              {dayColumns.map((col) => (
                <TableCell key={col.dayNum} className={dayHeaderCell}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isTablePending
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={i}>
                    <TableCell className={nameCell}>
                      <Skeleton />
                    </TableCell>
                    {dayColumns.map((col) => (
                      <TableCell key={col.dayNum} className={bodyCell} />
                    ))}
                  </TableRow>
                ))
              : radiologists.map((rad) => (
                  <AvailabilityTableRow
                    key={rad.id}
                    radiologist={rad}
                    dayColumns={dayColumns}
                    cutoffs={cutoffs}
                    isEditMode={isEditMode}
                    isEditable={editableRadiologistIds.includes(rad.id)}
                    getValue={getValue}
                    setValue={setValue}
                    getDisabledValues={getDisabledValues}
                    getSlotBounds={getSlotBounds}
                    nameCell={nameCell}
                    bodyCell={bodyCell}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConflictAlert
        open={conflictAlertOpen}
        conflicts={pendingConflicts}
        onEdit={handleConflictEdit}
        onConfirm={handleConflictConfirm}
        isLoading={isSaving}
      />
    </Box>
  );
};

export default RadiologistAvailabilityScreen;
